import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import useProfessionOptions from '../hooks/useProfessionOptions';
import { useAuth } from '../contexts/AuthContext';
import {
  ALL_PROFESSION,
  OTHER_PROFESSION,
  normalizeProfessionName,
} from '../utils/professions';

/**
 * Multi-select profession restrictions for Coupons.
 * Default = ALL (no restriction). Selecting any specific profession clears ALL.
 */
export default function ProfessionRestrictionSelect({
  selected = [ALL_PROFESSION],
  onChange,
  otherText = '',
  onOtherTextChange,
}) {
  const { user } = useAuth();
  const canManageList = ['app_admin', 'super_admin'].includes(user?.role);
  const { options, loading, adding, addProfession } = useProfessionOptions();
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const selectedSet = useMemo(
    () => new Set((selected || []).map(normalizeProfessionName)),
    [selected],
  );
  const allSelected =
    selectedSet.size === 0 || selectedSet.has(ALL_PROFESSION);
  const otherSelected = selectedSet.has(OTHER_PROFESSION);

  // Keep previously saved custom professions visible even if not in API list yet
  const displayOptions = useMemo(() => {
    const set = new Set(options);
    const extras = [...selectedSet].filter(
      (p) =>
        p &&
        p !== ALL_PROFESSION &&
        p !== OTHER_PROFESSION &&
        !set.has(p),
    );
    return extras.length ? [...options, ...extras] : options;
  }, [options, selectedSet]);

  const emit = (next) => {
    if (typeof onChange === 'function') onChange(next);
  };

  const handleToggleAll = (checked) => {
    if (checked) {
      emit([ALL_PROFESSION]);
      if (typeof onOtherTextChange === 'function') onOtherTextChange('');
    } else {
      // Unchecking ALL alone keeps ALL (must pick a specific profession)
      emit([ALL_PROFESSION]);
    }
  };

  const handleToggleProfession = (profession, checked) => {
    const name = normalizeProfessionName(profession);
    if (checked) {
      const withoutAll = (selected || [])
        .map(normalizeProfessionName)
        .filter((p) => p && p !== ALL_PROFESSION);
      if (!withoutAll.includes(name)) withoutAll.push(name);
      emit(withoutAll);
    } else {
      const next = (selected || [])
        .map(normalizeProfessionName)
        .filter((p) => p && p !== name && p !== ALL_PROFESSION);
      emit(next.length > 0 ? next : [ALL_PROFESSION]);
      if (name === OTHER_PROFESSION && typeof onOtherTextChange === 'function') {
        onOtherTextChange('');
      }
    }
  };

  const handleAddSubmit = async () => {
    const created = await addProfession(newName);
    if (created) {
      const withoutAll = (selected || [])
        .map(normalizeProfessionName)
        .filter((p) => p && p !== ALL_PROFESSION);
      if (!withoutAll.includes(created)) withoutAll.push(created);
      emit(withoutAll);
      setNewName('');
      setAddOpen(false);
    }
  };

  const specificSelected = allSelected
    ? []
    : [...selectedSet].filter(
        (p) => p !== ALL_PROFESSION && p !== OTHER_PROFESSION,
      );

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Default is <strong>ALL</strong> — coupon works for every profession.
        Select one or more professions to restrict (e.g. STUDENT-only Apar ID
        card). Optional — leave ALL if no restriction needed.
        <br />
        <strong>Use cases:</strong> DRIVER → EHSRAM; RETIRED GOVT EMPLOYEE → EHS
        CARD; STUDENT → Apar ID / student offers.
      </Alert>

      {loading && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Loading profession list…
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              onChange={(e) => handleToggleAll(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography fontWeight={700}>
              ALL{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                (default — no restriction)
              </Typography>
            </Typography>
          }
        />

        {displayOptions.map((profession) => (
          <FormControlLabel
            key={profession}
            control={
              <Checkbox
                checked={!allSelected && selectedSet.has(profession)}
                onChange={(e) =>
                  handleToggleProfession(profession, e.target.checked)
                }
              />
            }
            label={profession}
          />
        ))}

        <FormControlLabel
          control={
            <Checkbox
              checked={!allSelected && otherSelected}
              onChange={(e) =>
                handleToggleProfession(OTHER_PROFESSION, e.target.checked)
              }
            />
          }
          label="Other (not listed)"
        />
      </Box>

      {otherSelected && !allSelected && (
        <TextField
          fullWidth
          size="small"
          sx={{ mt: 1.5 }}
          label="Custom profession"
          placeholder="Type profession name"
          value={otherText}
          onChange={(e) =>
            typeof onOtherTextChange === 'function' &&
            onOtherTextChange(e.target.value)
          }
        />
      )}

      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {canManageList && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
        >
          Add Profession
        </Button>
        )}
        {!allSelected && specificSelected.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            Selected: {specificSelected.join(', ')}
            {otherSelected && otherText
              ? `, ${normalizeProfessionName(otherText)}`
              : ''}
          </Typography>
        )}
        {allSelected && (
          <Typography variant="caption" color="success.main">
            Selected: ALL professions
          </Typography>
        )}
      </Box>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Profession</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Profession name"
            placeholder="e.g. NURSE, TEACHER"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddSubmit();
              }
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Added professions appear in Coupons and Smart Bill lists.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddSubmit}
            disabled={adding || !newName.trim()}
          >
            {adding ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

